import queryString from 'query-string'

import { getAuthorizationToken } from './getAuthorizationToken'

const API_NAME = 'civicChampsApi'

const baseUrl = () => (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/+$/, '')

const buildUrl = (path, queryStringParameters) => {
  let url = `${baseUrl()}/${path.replace(/^\/+/, '')}`
  if (queryStringParameters && Object.keys(queryStringParameters).length > 0) {
    const filtered = {}
    for (const [key, raw] of Object.entries(queryStringParameters)) {
      if (raw === undefined || raw === null) continue
      filtered[key] = raw
    }
    const qs = queryString.stringify(filtered)
    if (qs) {
      url += url.includes('?') ? `&${qs}` : `?${qs}`
    }
  }
  return url
}

const isRawBody = body =>
  typeof body === 'string' || body instanceof FormData || body instanceof Blob || body instanceof URLSearchParams

const parseErrorBody = async response => {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    try {
      return await response.json()
    } catch {
      return null
    }
  }
  try {
    return await response.text()
  } catch {
    return null
  }
}

const request = async (method, apiName, path, config = {}) => {
  if (apiName !== API_NAME) {
    throw new Error(`Unknown API: ${apiName}`)
  }

  const { body, headers = {}, queryStringParameters, responseType } = config

  const authorization = await getAuthorizationToken()
  const init = {
    method: method.toUpperCase(),
    headers: { ...headers },
  }
  if (authorization) {
    init.headers.Authorization = authorization
  }

  if (body !== undefined && body !== null && method.toLowerCase() !== 'get') {
    if (isRawBody(body)) {
      // fetch must set multipart Content-Type itself so the boundary is included
      init.body = body
    } else {
      init.headers['Content-Type'] = init.headers['Content-Type'] || 'application/json'
      init.body = JSON.stringify(body)
    }
  }

  let response
  try {
    response = await fetch(buildUrl(path, queryStringParameters), init)
  } catch (e) {
    const networkError = new Error('Network Error')
    throw networkError
  }

  if (!response.ok) {
    const data = await parseErrorBody(response)
    const error = new Error(`${method.toUpperCase()} ${path} failed: ${response.status}`)
    error.response = { status: response.status, data }
    throw error
  }

  if (responseType === 'blob') {
    return response.blob()
  }

  if (response.status === 204) {
    return null
  }

  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    return response.json()
  }

  return response.text()
}

/** Drop-in replacement for Amplify v4 `API` used by onboarding and shared helpers. */
export const API = {
  get: (apiName, path, config) => request('get', apiName, path, config),
  post: (apiName, path, config) => request('post', apiName, path, config),
  put: (apiName, path, config) => request('put', apiName, path, config),
  patch: (apiName, path, config) => request('patch', apiName, path, config),
  del: (apiName, path, config) => request('del', apiName, path, config),
}
